import React, { FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PanelItem } from '@components/panels/PanelItems';
import { HorizontalLineSep } from '@components/separators/horizontal_line';
import { NavBar } from "@components/navigation/NavBar/NavBar";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import { Country } from "@utils/constants/localisation";
import { countrySpecificCurrency, countrySpecificDistanceUnit } from '@utils/misc/functions';
import { OnboardingPersonalState } from './onboardingPersonalReducer';
import { OnboardingAddressState } from './onboardingAddressReducer';
import { OnboardingDriverLicenceState } from './onboardingDriverLicenceReducer';
import { OnboardingEmergencyContactsState } from './onboardingEmergencyContactsReducer';
import { OnboardingAgreementState } from './onboardingAgreementReducer';
import { OnboardingInsuranceState } from './onboardingInsuranceReducer';
import { OnboardingVehicleState } from './onboardingVehicleReducer';

interface FlexiOwnOnboardingReviewV2Props {
  navigateTarget: string;
  onboardingPersonalState: OnboardingPersonalState;
  onboardingAddressState: OnboardingAddressState;
  onboardingDriverLicenceState: OnboardingDriverLicenceState;
  onboardingEmergencyContactsState: OnboardingEmergencyContactsState;
  onboardingAgreementState: OnboardingAgreementState;
  onboardingInsuranceState: OnboardingInsuranceState;
  onboardingVehicleState: OnboardingVehicleState;
  country: Country;
  custId: string;
}

export default function FlexiOwnOnboardingReviewV2({
  navigateTarget,
  onboardingPersonalState,
  onboardingAddressState,
  onboardingDriverLicenceState,
  onboardingEmergencyContactsState,
  onboardingAgreementState,
  onboardingInsuranceState,
  onboardingVehicleState,
  country,
  custId
}: FlexiOwnOnboardingReviewV2Props) {
  const navigate = useNavigate();

  const formatAddress = (): string => {
    switch (country) {
      case Country.GB:
        return `${onboardingAddressState.street} ${onboardingAddressState.city} ${onboardingAddressState.postcode}`;
      case Country.AU:
        return `${onboardingAddressState.street} ${onboardingAddressState.city} ${onboardingAddressState.state} ${onboardingAddressState.postcode}`;
      default:
        return '';
    }
  };

  const distUnit = countrySpecificDistanceUnit(country);
  const currencySymbol = countrySpecificCurrency(country);

  const hasInsurancePriceBeat = onboardingInsuranceState.hasOtherInsurance === 'true';
  // Discount amount parsed the same way as ipb_discount_amount in InvoicePreviewV2,
  // so the fee shown here matches ipb_discounted_weekly_subscription in the contract
  const ipbDiscountAmount = hasInsurancePriceBeat && onboardingInsuranceState.couponCode
    ? parseInt(onboardingInsuranceState.couponCode.match(/[\d.]+/)?.[0] || '0')
    : 0;
  const baseWeeklyFee = `${currencySymbol}${onboardingAgreementState.planWeeklyFee.toFixed(2)}`;
  const weeklyFeeDisplay = ipbDiscountAmount > 0
    ? `${currencySymbol}${(onboardingAgreementState.planWeeklyFee - ipbDiscountAmount).toFixed(2)} (${baseWeeklyFee} less ${currencySymbol}${ipbDiscountAmount} insurance price beat discount)`
    : baseWeeklyFee;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    // TODO: Add payload
    console.log('Final submission of all onboarding data:', {
      personal: onboardingPersonalState,
      address: onboardingAddressState,
      driverLicence: onboardingDriverLicenceState,
      emergencyContacts: onboardingEmergencyContactsState,
      agreement: onboardingAgreementState,
      insurance: onboardingInsuranceState,
      vehicle: onboardingVehicleState
    });
    navigate(navigateTarget);
  };

  return (
    <>
      <NavBar pageTitle={"Review and Submit"} backAction={undefined} />
      <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={onSubmit}>
        <div style={{ margin: '2.5vh 0' }}>
          <h3>Please review all details before submitting</h3>
          <p>Ensure all information is correct before final submission.</p>
        </div>

        <div style={{ margin: '2.5vh 0', border: '1px solid #E0E0E0', borderRadius: '8px', padding: '20px', backgroundColor: '#FAFAFA' }}>
          <h4 style={{ marginTop: 0 }}>Personal Details</h4>
          <PanelItem header='First name' headerTextStyleCfg={{ bold: true }} value={onboardingPersonalState.firstName} />
          <PanelItem header='Last name' headerTextStyleCfg={{ bold: true }} value={onboardingPersonalState.lastName} />
          <PanelItem header='Email' headerTextStyleCfg={{ bold: true }} value={onboardingPersonalState.email} />
          <PanelItem header='Gender' headerTextStyleCfg={{ bold: true }} value={onboardingPersonalState.gender} />
          <PanelItem header='Nationality' headerTextStyleCfg={{ bold: true }} value={onboardingPersonalState.nationality} />
          {country === Country.AU ? (
            <PanelItem header='Hirer ABN' headerTextStyleCfg={{ bold: true }} value={onboardingPersonalState.hirerABN || 'Not provided'} />
          ) : (
            <PanelItem header='National Insurance Number' headerTextStyleCfg={{ bold: true }} value={onboardingPersonalState.nationalInsuranceNumber || 'Not provided'} />
          )}
          {onboardingPersonalState.alternativeName && (
            <PanelItem header='Alternative Name' headerTextStyleCfg={{ bold: true }} value={onboardingPersonalState.alternativeName} />
          )}
        </div>

        <div style={{ margin: '2.5vh 0', border: '1px solid #E0E0E0', borderRadius: '8px', padding: '20px', backgroundColor: '#FAFAFA' }}>
          <h4 style={{ marginTop: 0 }}>Address</h4>
          <PanelItem header='Address' headerTextStyleCfg={{ bold: true }} value={formatAddress()} />
        </div>

        <div style={{ margin: '2.5vh 0', border: '1px solid #E0E0E0', borderRadius: '8px', padding: '20px', backgroundColor: '#FAFAFA' }}>
          <h4 style={{ marginTop: 0 }}>Driver Licence</h4>
          <PanelItem header='Driver licence number' headerTextStyleCfg={{ bold: true }} value={onboardingDriverLicenceState.driverLicenceNumber} />
          {country === Country.AU && onboardingDriverLicenceState.licenceState && (
            <PanelItem header='State' headerTextStyleCfg={{ bold: true }} value={onboardingDriverLicenceState.licenceState} />
          )}
          <PanelItem header='Expiry date' headerTextStyleCfg={{ bold: true }} value={onboardingDriverLicenceState.licenceExpiry} />
          <PanelItem header='Date of birth' headerTextStyleCfg={{ bold: true }} value={onboardingDriverLicenceState.dob} />
        </div>

        <div style={{ margin: '2.5vh 0', border: '1px solid #E0E0E0', borderRadius: '8px', padding: '20px', backgroundColor: '#FAFAFA' }}>
          <h4 style={{ marginTop: 0 }}>Emergency Contacts</h4>
          <PanelItem header='Primary contact name' headerTextStyleCfg={{ bold: true }} value={onboardingEmergencyContactsState.primaryName} />
          <PanelItem header='Primary phone' headerTextStyleCfg={{ bold: true }} value={onboardingEmergencyContactsState.primaryPhone} />
          <PanelItem header='Primary relationship' headerTextStyleCfg={{ bold: true }} value={onboardingEmergencyContactsState.primaryRelationship} />
          <PanelItem header='Primary address' headerTextStyleCfg={{ bold: true }} value={onboardingEmergencyContactsState.primaryAddress} />
          {onboardingEmergencyContactsState.secondaryName && (
            <>
              <HorizontalLineSep />
              <PanelItem header='Secondary contact name' headerTextStyleCfg={{ bold: true }} value={onboardingEmergencyContactsState.secondaryName} />
              <PanelItem header='Secondary phone' headerTextStyleCfg={{ bold: true }} value={onboardingEmergencyContactsState.secondaryPhone} />
              <PanelItem header='Secondary relationship' headerTextStyleCfg={{ bold: true }} value={onboardingEmergencyContactsState.secondaryRelationship} />
              <PanelItem header='Secondary address' headerTextStyleCfg={{ bold: true }} value={onboardingEmergencyContactsState.secondaryAddress} />
            </>
          )}
        </div>

        {onboardingVehicleState.vehicleId && (
          <div style={{ margin: '2.5vh 0', border: '1px solid #E0E0E0', borderRadius: '8px', padding: '20px', backgroundColor: '#FAFAFA' }}>
            <h4 style={{ marginTop: 0 }}>Vehicle Details</h4>
            <PanelItem header='Make' headerTextStyleCfg={{ bold: true }} value={onboardingVehicleState.make} />
            <PanelItem header='Model' headerTextStyleCfg={{ bold: true }} value={onboardingVehicleState.model} />
            <PanelItem header='Year' headerTextStyleCfg={{ bold: true }} value={onboardingVehicleState.year} />
            <PanelItem header='Color' headerTextStyleCfg={{ bold: true }} value={onboardingVehicleState.color} />
            <PanelItem header='License Plate' headerTextStyleCfg={{ bold: true }} value={onboardingVehicleState.licensePlate} />
            <PanelItem header='VIN' headerTextStyleCfg={{ bold: true }} value={onboardingVehicleState.vin} />
            {onboardingVehicleState.odometer > 0 && (
              <PanelItem header={`Odometer (${distUnit})`} headerTextStyleCfg={{ bold: true }} value={onboardingVehicleState.odometer} />
            )}
          </div>
        )}

        <div style={{ margin: '2.5vh 0', border: '1px solid #E0E0E0', borderRadius: '8px', padding: '20px', backgroundColor: '#FAFAFA' }}>
          <h4 style={{ marginTop: 0 }}>Agreement Schedule</h4>
          <PanelItem header='Contract execution date' headerTextStyleCfg={{ bold: true }} value={onboardingAgreementState.executionDate} />
          <PanelItem header='Subscription start date' headerTextStyleCfg={{ bold: true }} value={onboardingAgreementState.subsStartDate} />
          <PanelItem header='Subscription end date' headerTextStyleCfg={{ bold: true }} value={onboardingAgreementState.subsEndDate} />

          <PanelItem header='Plan length (years)' headerTextStyleCfg={{ bold: true }} value={onboardingAgreementState.planLen} />
          <PanelItem header='Contact Owner' headerTextStyleCfg={{ bold: true }} value={onboardingAgreementState.contactOwner} />
        </div>

        <div style={{ margin: '2.5vh 0', border: '1px solid #E0E0E0', borderRadius: '8px', padding: '20px', backgroundColor: '#F0F8FF' }}>
          <h4 style={{ marginTop: 0 }}>Pricing Breakdown</h4>
          <PanelItem header='Minimum period (weeks)' headerTextStyleCfg={{ bold: true }} value={onboardingAgreementState.minimumTerm} />
          <PanelItem
            header={`Weekly hire fee`}
            headerTextStyleCfg={{ bold: true }}
            value={weeklyFeeDisplay}
          />
          <PanelItem
            header={`Weekly distance allowance (${distUnit})`}
            headerTextStyleCfg={{ bold: true }}
            value={onboardingAgreementState.planWeeklyDistAllowance}
          />
          <PanelItem
            header={`Excess distance charge per ${distUnit} (${currencySymbol})`}
            headerTextStyleCfg={{ bold: true }}
            value={Number(onboardingAgreementState.excessDistanceCost || 0).toFixed(2)}
          />
          <PanelItem
            header={`Setup fee (${currencySymbol})`}
            headerTextStyleCfg={{ bold: true }}
            value={onboardingAgreementState.planSetupFee}
          />
          {onboardingAgreementState.planInitialContribution && (
            <PanelItem
              header={`Initial contribution (${currencySymbol})`}
              headerTextStyleCfg={{ bold: true }}
              value={onboardingAgreementState.planInitialContribution}
            />
          )}
          {hasInsurancePriceBeat && onboardingInsuranceState.couponCode && (
            <>
              <HorizontalLineSep />
              <PanelItem
                header='Insurance Price Beat'
                headerTextStyleCfg={{ bold: true }}
                value='Applied'
              />
              <PanelItem
                header='Coupon Code'
                headerTextStyleCfg={{ bold: true }}
                value={onboardingInsuranceState.couponCode}
              />
            </>
          )}
        </div>

        <div style={{ margin: '4vh 0' }}>
          <p style={{ fontSize: '0.9em', color: '#666' }}>
            By submitting this form, you confirm that all information provided is accurate and complete.
          </p>
        </div>

        <SubmitBtn text={"Invoice Preview"} />
      </form>
    </>
  );
}